// ============= Imports =============
import { useEffect, useCallback } from 'react';
import { useSignalRHub } from '@/hooks/useSignalRHub';
import {
  createAskRelayHubHandlers,
  AskRelayHubOptions,
} from '@/handlers/signalR/askRelayHub';
import { useToastSafe } from '@/hooks/signalR/useToastSafe';
import { addClientToGroupAskSession } from '@/apis/core/messaging/askMessageRelayHub/post/addClientToGroupAskSession';
import { useSignalR } from '@/contexts/signalR';
import { UseAuth } from '@/contexts/AuthContext';
import { useWorkspaceErrorHandler } from '@/hooks/useWorkspaceErrorHandler';

// ============= Hook =============

/**
 * React hook that wires Ask relay hub SignalR handlers into the current component.
 *
 * This hook:
 * - Subscribes to all Ask-related SignalR events exposed by `createAskRelayHubHandlers`.
 * - Optionally filters by `workspaceId` and `askSessionId`.
 * - Binds toast helpers (if available) for user feedback.
 * - Joins the Ask session SignalR group (via AddClientToUsersAsync) when a workspaceId is provided.
 *
 * @param options - Configuration for Ask relay hub handling (filters, callbacks, logging, toasts).
 */

export const useAskRelayHubHandlers = (options: AskRelayHubOptions = {}) => {
  const askHub = useSignalRHub({ hubName: 'askRelayHub' });
  const { invokeHub } = useSignalR();
  const { currentUser } = UseAuth();
  const { handleError } = useWorkspaceErrorHandler(options.askSessionId);
  const enableToasts = options.enableToasts !== false; // Default to true
  const toasts = useToastSafe(enableToasts);

  useEffect(() => {
    if (!askHub.isConnected || !askHub.on || !askHub.off) {
      return;
    }

    // When loading an Ask session, ensure the client joins the SignalR group
    // for that workspace so it receives AskSession-specific events.
    if (options.askSessionId && currentUser?.userId) {
      addClientToGroupAskSession({
        invokeHub,
        askSessionId: options.askSessionId,
        userId: currentUser.userId,
      }).catch((err) => {
        const error = err instanceof Error ? err : new Error(String(err));
        const response =
          (err as { response?: unknown; body?: unknown })?.response ??
          (err as { body?: unknown })?.body;

        console.error(
          `Failed to add client to Ask session group [${options.askSessionId}]:`,
          error
        );
        handleError(
          error,
          'Failed to subscribe to live Ask session updates',
          response
        );
      });
    }

    const { subscribe, unsubscribe } = createAskRelayHubHandlers(
      {
        on: askHub.on,
        off: askHub.off,
      },
      {
        ...options,
        toasts,
      }
    );

    try {
      subscribe();
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error('Failed to subscribe Ask relay hub handlers:', error);
      handleError(error, 'Failed to subscribe to Ask session updates');
    }

    return () => {
      unsubscribe();
    };
  }, [
    askHub.isConnected,
    askHub.on,
    askHub.off,
    invokeHub,
    handleError,
    options.askSessionId,
    options.userId,
    options.onDataRefresh,
    options.onMessageReceived,
    options.onEventReceived,
    options.enableToasts,
    options.enableLogging,
    enableToasts,
    toasts,
    currentUser?.userId,
  ]);

  const ensureAskSessionGroupJoin = useCallback(async () => {
    if (!askHub.isConnected || !options.askSessionId || !currentUser?.userId) {
      return;
    }

    try {
      await addClientToGroupAskSession({
        invokeHub,
        askSessionId: options.askSessionId,
        userId: currentUser.userId,
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      const response =
        (err as { response?: unknown; body?: unknown })?.response ??
        (err as { body?: unknown })?.body;

      console.error(
        `Failed to ensure client is in Ask session group [${options.askSessionId}]:`,
        error
      );
      handleError(
        error,
        'Failed to subscribe to live Ask session updates',
        response
      );
    }
  }, [
    askHub.isConnected,
    invokeHub,
    handleError,
    options.askSessionId,
    currentUser?.userId,
  ]);

  return {
    isConnected: askHub.isConnected,
    ensureAskSessionGroupJoin,
  };
};

